// ---------- la pièce d'une plus grande image ----------
// La première feuille d'un nouveau joueur, sortie du prototype de src/essai.ts (2026-10-29) pour
// devenir un vrai module. Le bilan qui l'a demandée (sauvegarde de production du 2026-10-30) :
// 37 % des joueurs qui posent une case ne finissent jamais de feuille, et ceux qui en finissent
// une reviennent le lendemain à 61 % contre 17 %. Une première feuille doit donc se finir en
// cinq minutes — mais à cette taille un dessin ressemble à du pixel art. L'effet ne vient pas de
// la finesse, il vient de la mise en scène.
//
// Le joueur peint une PIÈCE, un rectangle découpé dans une grande grille (l'œil du chien, pour
// l'initiation). À sa dernière case, la grande grille prend sa place sans couture, cadrée pour
// que ses cases tombent exactement sur celles qu'il vient de peindre, puis la caméra recule
// jusqu'à la montrer entière. La grande feuille est déjà commencée, et il sait comment on fait.
//
// UN DÉCOUPAGE ET NON UNE FENÊTRE OUVERTE DANS LA GRANDE GRILLE, parce qu'il ne coûte rien au
// moteur : pendant la pièce, tout marche tel quel (palette, zones, boussole, compte des cases),
// il n'y a ni voile à poser sur la feuille ni tap hors fenêtre à refuser. Le seul prix est la
// sauvegarde, qui doit parler en cases de la GRANDE grille — voir piecePersist().
//
// Une liste de pièces suivra (le mode « deviner le sujet », les chapitres d'une grande feuille,
// cf. IDEES.md) ; ce module n'en joue qu'une, et ses fonctions de conversion d'indices sont déjà
// celles dont une suite aura besoin.
//
// Les dépendances vont vers ui.ts et render.ts, jamais dans l'autre sens : ui.ts ne connaît la
// pièce que par deux crochets (setFinishOverride, setPersistOverride), posés ici. Rien ne ferme
// de cycle d'import, et le module se débranche d'une ligne.

import { anim, animBurst, animFlyTo, animNow, animWake, noMotion } from './anim';
import { t } from './i18n';
import { dismissPieceDone, foxPeek, openPieceDone } from './mascot';
import { saveProgress } from './progress';
import {
  handBackView,
  invalidateHalo,
  invalidateLayers,
  render,
  renderThumbOf,
  resetPlay,
  updateProgress,
  wholeView,
} from './render';
import { S, ZOOM_MAX } from './state';
import {
  afterLayout,
  buildLegend,
  setFinishOverride,
  setPersistOverride,
  setSheetLocked,
  startPlay,
} from './ui';

/** Un rectangle de la grande grille, en cases. */
export interface PieceRect {
  r0: number;
  c0: number;
  w: number;
  h: number;
}

/** La grande feuille dont la pièce est découpée. `puzzleId` nul : une feuille d'essai, que rien
 *  ne sauvegarde (cf. essai.ts). */
export interface BigSheet {
  puzzleId: string | null;
  fp: string;
  alphabetId: string;
  gridW: number;
  gridH: number;
  target: number[][];
}

export interface PieceOptions {
  /** Le dessin entier en transparence à la fin du recul. Oui pour l'initiation, où il dit
   *  l'enjeu ; non pour un mode où il faut deviner le sujet, qu'il donnerait. */
  ghost: boolean;
  /** La phrase posée sur la feuille à la fin. */
  line: string;
  /**
   * La fin de l'initiation : au lieu de la phrase, le renard félicite, montre la vitrine du jeu et
   * demande la suite (openPieceDone dans mascot.ts) — « Terminer ce dessin » ramène la caméra
   * sur des cases à faire, « Voir la bibliothèque » appelle ceci. Fourni par main.ts : ouvrir le
   * catalogue demande home.ts, qui importe ce module.
   */
  onLibrary?: () => void;
  /** Les étapes que la pièce traverse, pour qui les compte (l'entonnoir de l'initiation, cf.
   *  src/introStats.ts) : la première case posée, la pièce finie, le choix devant le renard. */
  onStep?: (step: 'started' | 'pieceDone' | 'finish' | 'library') => void;
}

// ---------- les indices : pur, et testé (tests/piece.test.ts) ----------

/** Un carré de `side` cases centré sur (fx, fy), en fractions de la grille, et ramené dans la
 *  grille s'il en déborde — le cadrage du prototype. */
export function centredRect(
  gridW: number,
  gridH: number,
  side: number,
  fx: number,
  fy: number,
): PieceRect {
  const n = Math.min(side, gridW, gridH);
  const clamp = (v: number, hi: number) => Math.max(0, Math.min(hi, v));
  return {
    r0: clamp(Math.round(fy * gridH - n / 2), gridH - n),
    c0: clamp(Math.round(fx * gridW - n / 2), gridW - n),
    w: n,
    h: n,
  };
}

/** Les codes de la pièce. */
export function cropTarget(target: readonly (readonly number[])[], rect: PieceRect): number[][] {
  return target.slice(rect.r0, rect.r0 + rect.h).map((row) => row.slice(rect.c0, rect.c0 + rect.w));
}

/** Un tap de la pièce, en indice de la grande grille. */
export function pieceToBig(idx: number, rect: PieceRect, bigW: number): number {
  return (Math.floor(idx / rect.w) + rect.r0) * bigW + (idx % rect.w) + rect.c0;
}

/** Les taps d'une sauvegarde de la grande grille qui tombent dans la pièce, en indices de la
 *  pièce et dans leur ordre. Ceux du dehors sont ignorés : ils ne se rejouent que sur la grande. */
export function bigToPiece(taps: readonly number[], rect: PieceRect, bigW: number): number[] {
  const out: number[] = [];
  for (const idx of taps) {
    const r = Math.floor(idx / bigW) - rect.r0;
    const c = (idx % bigW) - rect.c0;
    if (r >= 0 && r < rect.h && c >= 0 && c < rect.w) out.push(r * rect.w + c);
  }
  return out;
}

/** La pièce est-elle entièrement peinte dans cette sauvegarde de la grande grille ? Alors on ne
 *  la rejoue pas : on ouvre directement la grande. */
export function pieceDone(
  target: readonly (readonly number[])[],
  rect: PieceRect,
  taps: readonly number[],
  bigW: number,
): boolean {
  const painted = new Set(taps);
  for (let r = rect.r0; r < rect.r0 + rect.h; r++)
    for (let c = rect.c0; c < rect.c0 + rect.w; c++)
      if (target[r][c] && !painted.has(r * bigW + c)) return false;
  return true;
}

// ---------- la partie ----------

/** Le temps de regarder la petite feuille finie avant que la grande prenne sa place : la
 *  dernière zone a fini d'éclore, et l'œil a le temps de lire ce qu'il vient de faire. */
const HOLD_MS = 1300;
/** Le temps où la grande feuille est déjà là, cadrée sur la petite, avant que la caméra parte :
 *  sans ce temps mort, le recul commence avant que l'œil ait vu que rien n'a bougé. */
const STILL_MS = 450;
/** Le recul lui-même. Lent exprès : c'est LE moment de la feuille, pas un déplacement. */
const PULL_MS = 3200;
/** Le fantôme du dessin entier, posé sur la feuille à l'arrivée : montée, tenue, effacement.
 *  Et son opacité au plus haut — assez pour reconnaître le sujet, pas assez pour croire qu'il
 *  est déjà peint. */
const GHOST_IN_MS = 700;
const GHOST_HOLD_MS = 1300;
const GHOST_OUT_MS = 1100;
const GHOST_ALPHA = 0.32;

interface Active {
  big: BigSheet;
  rect: PieceRect;
  opts: PieceOptions;
  /** Les taps de la grande sauvegarde HORS de la pièce : vides à l'initiation, gardés pour
   *  qu'une sauvegarde de la pièce n'efface jamais ce qui a été peint ailleurs. */
  outside: number[];
  /** Les cases à peindre de la grande grille, pour le pourcentage de la carte. */
  bigTodo: number;
}
let active: Active | null = null;

/**
 * Ouvre la pièce `rect` de `big`, en reprenant `savedTaps` (une sauvegarde de la GRANDE grille).
 *
 * Ne ferme pas le catalogue : c'est à l'appelant, qui sait d'où il vient (home.ts importe ce
 * module, l'inverse fermerait un cycle). Si la pièce est déjà finie dans la sauvegarde — le
 * joueur a quitté pendant le recul —, rend faux sans rien ouvrir : l'appelant ouvre la grande
 * feuille comme n'importe quelle autre.
 */
export function openPiece(
  big: BigSheet,
  rect: PieceRect,
  savedTaps: readonly number[],
  opts: PieceOptions,
): boolean {
  if (big.puzzleId && pieceDone(big.target, rect, savedTaps, big.gridW)) return false;
  const inside = new Set<number>();
  for (let r = rect.r0; r < rect.r0 + rect.h; r++)
    for (let c = rect.c0; c < rect.c0 + rect.w; c++) inside.add(r * big.gridW + c);
  active = {
    big,
    rect,
    opts,
    outside: savedTaps.filter((t) => !inside.has(t)),
    bigTodo: big.target.flat().filter((v) => v > 0).length,
  };

  // `puzzleId` reste nul pendant la pièce : tout le reste du jeu (synchronisation, vignettes,
  // carte de fin, badges) la traite comme la feuille de repli, qui n'appartient à personne. Seule
  // la sauvegarde la connaît, par le crochet ci-dessous.
  S.puzzleId = null;
  S.puzzleFp = '';
  S.alphabetId = big.alphabetId;
  S.gridW = rect.w;
  S.gridH = rect.h;
  S.target = cropTarget(big.target, rect);
  S.viewport = { z: 1, x: 0, y: 0 };
  S.lockedSymbol = null;
  S.hover = null;
  S.hoverKey = '';
  buildLegend();
  invalidateLayers();
  startPlay();

  // La reprise, comme restore() dans home.ts : une affectation silencieuse, sans son ni score.
  for (const idx of bigToPiece(savedTaps, rect, big.gridW)) {
    const r = Math.floor(idx / rect.w);
    const c = idx % rect.w;
    if (!S.target[r][c] || S.filled[r][c]) continue;
    S.filled[r][c] = true;
    S.taps.push(idx);
  }
  if (S.taps.length) {
    invalidateLayers();
    render();
    updateProgress();
  }

  setPersistOverride(piecePersist);
  setFinishOverride(() => {
    setFinishOverride(null);
    opts.onStep?.('pieceDone');
    reveal();
    return true;
  });
  const opened = active;
  window.setTimeout(() => {
    if (active === opened && !S.gameOver) comfortZoom();
  }, COMFORT_AFTER_MS);
  return true;
}

/**
 * La taille de case, en pixels CSS, à laquelle la pièce s'offre au doigt (2026-10-31).
 *
 * Feuille entière, l'œil de 38 × 38 donne des cases de 10 px sur un téléphone de 412 px : elles
 * se peignent (la pièce est sous DETAIL_MAX_CELLS), mais on vise au millimètre, et le premier
 * contact avec le jeu est une suite de taps ratés. 26 px, c'est la pulpe d'un doigt — la taille
 * des cibles tactiles courantes, moins la marge qu'un remplissage par zone rend inutile : viser
 * à côté dans la même zone remplit quand même.
 */
const COMFORT_PX = 26;
/** On montre d'abord la feuille entière, le temps de voir ce qu'on va remplir, puis la caméra
 *  plonge — et le dialogue des gestes (coach.ts, à 1,2 s) s'ouvre sur la feuille déjà cadrée. */
const COMFORT_AFTER_MS = 500;

/** Rapproche la caméra jusqu'à des cases confortables, sur des cases du symbole verrouillé. */
function comfortZoom(): void {
  const z = Math.min(ZOOM_MAX, COMFORT_PX / S.cellW);
  if (z <= S.viewport.z * 1.05) return;
  // Le centre de la pièce, puis, s'il n'y a rien à peindre là, la fenêtre la plus proche qui
  // tienne des cases du symbole — la même recherche que le retour après un palier.
  const mid = {
    z,
    x: S.availW / 2 - (S.pxW / 2) * z,
    y: S.availH / 2 - (S.pxH / 2) * z,
  };
  const to = handBackView(mid, S.lockedSymbol) ?? mid;
  animFlyTo(to.z, to.x, to.y);
}

/**
 * La sauvegarde pendant la pièce, à la place de celle de ui.ts : sous l'identifiant de la GRANDE
 * feuille, ses taps traduits en cases de la grande grille. Fermer l'application au milieu de la
 * pièce rouvre donc la pièce là où on l'a laissée, et une fois la grande feuille ouverte, rien
 * n'est à recopier — la sauvegarde est déjà la sienne.
 *
 * Jamais `done` : la pièce finie n'est pas la feuille finie. Pas de vignette non plus : celle de
 * la carte est la grande feuille, et le canevas ne montre que la pièce.
 */
function piecePersist(): boolean {
  const a = active;
  if (!a) return false;
  if (!a.big.puzzleId) return true;
  const taps = a.outside.concat(S.taps.map((i) => pieceToBig(i, a.rect, a.big.gridW)));
  const pct = a.bigTodo ? Math.floor((100 * taps.length) / a.bigTodo) : 0;
  // La vignette de la carte « Mon flux » (cf. introCard dans home.ts) : le chien ENTIER avec ce
  // qui en est peint, et non la pièce découpée qu'on a sous les yeux. Sans elle, la carte gardait
  // la première image reconstruite et ne suivait plus l'œil (revue du 2026-10-31). Prise à la
  // sortie de la feuille seulement (cf. saveProgress) : rien ne se dessine à chaque case.
  const { big } = a;
  const painted = new Set(taps);
  saveProgress(big.puzzleId!, big.fp, taps, pct, false, () =>
    renderThumbOf({
      gridW: big.gridW,
      gridH: big.gridH,
      target: big.target,
      alphabetId: big.alphabetId,
      filled: (r, c) => painted.has(r * big.gridW + c),
    }),
  );
  if (S.taps.length) a.opts.onStep?.('started');
  return true;
}

/** Les crochets de la pièce tombent ; la sauvegarde est déjà faite. */
function detach(): void {
  active = null;
  setPersistOverride(null);
  setFinishOverride(null);
}

/**
 * La mise en scène de fin (reveal → swapToBig → recul → fantôme → finale) court sur des
 * minuteurs, sept secondes et demie en tout. Chacun vérifie qu'il appartient encore à la
 * scène en cours : ouvrir une autre feuille au milieu l'annule (closePiece), sans quoi la
 * caméra de la NOUVELLE feuille reculait, le fantôme du chien se posait dessus et le renard
 * demandait « on termine ce dessin ? » à propos d'un autre (revue du 2026-10-31).
 */
let scene = 0;
/** Une scène a retenu le repère et verrouillé la feuille, et ne les a pas encore rendus. */
let staging = false;

/** Quitter la feuille pendant la pièce ou sa mise en scène de fin. */
export function closePiece(): void {
  detach();
  scene++;
  // Le renard qui attendait une réponse à propos de CETTE feuille se tait (cf. dismissPieceDone).
  dismissPieceDone();
  if (staging) unstage();
}

/** Rend ce que la scène a pris : le repère (en fondu), la feuille aux doigts. */
function unstage(): void {
  staging = false;
  setSheetLocked(false);
  anim.haloHeld = false;
  anim.haloIn = animNow();
  // Les compteurs de zones se taisaient eux aussi (cf. buildZones) : à refaire.
  invalidateHalo();
  // Le fondu se compte sur l'horloge de la boucle (cf. haloAlpha) : endormie, elle laisserait le
  // repère à zéro jusqu'au prochain geste qui la réveille — un pan ne le fait pas.
  animWake();
  render();
}

function reveal(): void {
  // Plus de fin à déclencher sur la petite feuille : elle est complète, et aucun tap ne peut
  // plus rien y remplir.
  S.gameOver = true;
  // Une gerbe des symboles de la feuille, comme la vraie fin (cf. celebrate dans ui.ts), mais
  // plus courte : ce n'est pas la fin, c'est le palier avant le vrai cadeau.
  const codes = S.target.flat().filter((m) => m > 0);
  for (let i = 0; i < 14; i++) {
    const cd = codes[Math.floor(Math.random() * codes.length)] ?? 1;
    window.setTimeout(() => animBurst(S.pxW / 2, S.pxH * 0.5, 1, cd, 4.2), i * 30);
  }
  const my = ++scene;
  window.setTimeout(() => {
    if (my === scene) swapToBig(my);
  }, HOLD_MS);
}

function swapToBig(my: number): void {
  const a = active;
  if (!a) return;
  const { big, rect } = a;
  // Où la petite feuille est À L'ÉCRAN : son origine et la largeur d'une case, en pixels. C'est
  // tout ce que la grande doit reproduire pour que le passage ne se voie pas (la hauteur suit :
  // les deux grilles ont le même alphabet, donc le même rapport de case).
  const v = { ...S.viewport };
  const cellW = S.cellW * v.z;
  const taps = a.outside.concat(S.taps.map((i) => pieceToBig(i, rect, big.gridW)));

  detach();
  S.puzzleId = big.puzzleId;
  S.puzzleFp = big.fp;
  S.alphabetId = big.alphabetId;
  S.gridW = big.gridW;
  S.gridH = big.gridH;
  S.target = big.target;
  buildLegend();
  invalidateLayers();
  // resetPlay() recalcule la case de la grande grille (render), et repart d'une feuille vide.
  resetPlay();
  // Le verrou s'est posé sur un symbole de la grande feuille : son repère attend la fin du recul
  // (cf. finale), sinon ses cases se hachurent en rose pendant qu'on montre le dessin.
  anim.haloHeld = !noMotion();
  // Et la feuille ne prend aucun doigt jusque-là : un pan se battait avec le vol de 3,2 s, un tap
  // peignait la grande feuille à l'aveugle (repère retenu), et un symbole fini ainsi lançait sa
  // propre récolte par-dessus le recul (revue du 2026-10-31).
  staging = true;
  setSheetLocked(true);
  // Les taps de la pièce, reportés dans la grande, dans l'ordre où ils ont été peints : c'est la
  // sauvegarde, et le rejeu de la grande feuille finie les rejouera en premier.
  for (const idx of taps) {
    const r = Math.floor(idx / big.gridW);
    const c = idx % big.gridW;
    if (!S.target[r]?.[c] || S.filled[r][c]) continue;
    S.filled[r][c] = true;
    S.taps.push(idx);
  }
  // Le cadrage qui pose la case (r0, c0) de la grande grille exactement sur l'ancienne origine,
  // à la même taille de case.
  const z = cellW / S.cellW;
  S.viewport = { z, x: v.x - rect.c0 * S.cellW * z, y: v.y - rect.r0 * S.cellH * z };
  // Les plaques de comptage des zones s'allument dès que la grande feuille est là, et le recul
  // est fait pour regarder, pas pour compter : on les pose d'emblée à leur opacité de repos,
  // comme si le joueur avait laissé la feuille tranquille (cf. platesAlpha dans render.ts). Ici
  // et pas au départ de la caméra — mesuré : dans le temps mort, un « 21 » couvrait l'œil. Le
  // premier geste les ramène, comme toujours.
  anim.fade = animNow() - 60_000;
  invalidateLayers();
  render();
  updateProgress();
  afterLayout();

  window.setTimeout(() => {
    if (my !== scene) return;
    const w = wholeView();
    animFlyTo(w.z, w.x, w.y, { ms: PULL_MS, dolly: true });
    window.setTimeout(() => {
      if (my !== scene) return;
      const end = (): void => finale(a.opts, my);
      if (a.opts.ghost) showGhost(big, end);
      else end();
    }, PULL_MS + 150);
  }, STILL_MS);
}

/**
 * Le dessin entier, en fantôme, le temps d'un souffle. Mesuré en capture le 2026-10-29 : sur un
 * alphabet sans numéros (la peinture), une case non peinte n'est que du papier, et le recul
 * arrivait sur un petit œil perdu dans une page blanche quadrillée. L'échelle se lisait, pas
 * l'enjeu. Le fantôme dit ce que la feuille cache : « voilà ce que tu vas faire apparaître ».
 *
 * Une image posée sur le canevas, exactement sur la feuille vue entière (wholeView, où le vol
 * vient d'arriver), en `pointer-events: none` comme tout ce qui se pose sur la feuille. Rastérisée
 * par renderThumbOf, le même dessin que la vignette du catalogue, toutes cases peintes.
 */
function showGhost(big: BigSheet, then: () => void): void {
  const box = document.querySelector<HTMLElement>('.sheetbox');
  const w = wholeView();
  const src = renderThumbOf(
    {
      gridW: big.gridW,
      gridH: big.gridH,
      target: big.target,
      alphabetId: big.alphabetId,
      filled: () => true,
    },
    Math.ceil(Math.max(S.pxW, S.pxH) * S.DPR),
  );
  if (!box || !src) {
    then();
    return;
  }
  // Le canevas n'est pas forcément collé au coin de la boîte (bordure) : on part de lui.
  const cv = S.sheet.getBoundingClientRect();
  const bx = box.getBoundingClientRect();
  const img = document.createElement('img');
  img.src = src;
  img.alt = '';
  Object.assign(img.style, {
    position: 'absolute',
    left: `${cv.left - bx.left - box.clientLeft + w.x}px`,
    top: `${cv.top - bx.top - box.clientTop + w.y}px`,
    width: `${S.pxW * w.z}px`,
    height: `${S.pxH * w.z}px`,
    pointerEvents: 'none',
    opacity: '0',
    transition: `opacity ${GHOST_IN_MS}ms ease-out`,
  });
  box.appendChild(img);
  requestAnimationFrame(() => (img.style.opacity = String(GHOST_ALPHA)));
  window.setTimeout(() => {
    img.style.transition = `opacity ${GHOST_OUT_MS}ms ease-in`;
    img.style.opacity = '0';
    window.setTimeout(() => img.remove(), GHOST_OUT_MS + 50);
    then();
  }, GHOST_IN_MS + GHOST_HOLD_MS);
}

/**
 * Ce qui suit le recul et le fantôme. Le repère du symbole verrouillé sur la grande feuille était
 * retenu depuis la bascule (cf. swapToBig) : il se lève ici, en fondu, quand le joueur reprend la
 * main — et pas avant, sinon les cases de la suite se hachuraient en rose pendant le recul, sous
 * le dessin qu'on était en train de montrer.
 */
function finale(opts: PieceOptions, my: number): void {
  if (my !== scene) return;
  // Le joueur est parti au catalogue pendant la scène (« précédent ») : le renard attend son
  // retour sur la feuille, plutôt que de parler par-dessus la bibliothèque.
  if (document.querySelector('.home.on')) {
    window.setTimeout(() => finale(opts, my), 400);
    return;
  }
  const onLibrary = opts.onLibrary;
  if (!onLibrary) {
    unstage();
    showLine(opts.line);
    return;
  }
  openPieceDone((choice, pressed) => {
    // « précédent » referme sur « terminer » sans que le joueur l'ait choisi : l'entonnoir ne
    // compte que les boutons pressés, le reste de l'œil fini se lit comme « congédié ».
    if (pressed) opts.onStep?.(choice);
    unstage();
    if (choice === 'library') onLibrary();
    else comfortZoom();
  });
}

/** Sans choix à proposer (une pièce d'essai, cf. essai.ts) : la phrase qui dit ce qui vient de se
 *  passer, dite par le renard au bas de la feuille (foxPeek). C'est elle qui fait de la pièce une
 *  ÉTAPE et non une fin — un « Bravo » seul se lirait comme « c'est fini ». */
function showLine(line: string): void {
  foxPeek(t('piece.bravo'), line);
}
